// Reports wire contract — REST (§4/§5/§7), internal worker routes (§6.1) and
// run-tool answers. Every response the API sends for reports parses with one
// of these schemas; web, worker and the Goblic-MCP import the types from
// here. Endpoint list: docs/plans/2026-10-02-rapporten-contract.md.

import { z } from 'zod'
import { ReportAssetContentTypeSchema, ReportAssetNameSchema } from './assets.js'
import { ReportLastSubmissionSchema, ReportSubmissionDataSchema } from './bridge.js'
import {
  REPORT_MAX_ASSET_BYTES,
  REPORT_MAX_HTML_BYTES,
  REPORT_MESSAGE_MAX_LENGTH,
  REPORT_SUMMARY_MAX_LENGTH,
  REPORT_TITLE_MAX_LENGTH,
  reportByteLength,
} from './limits.js'

// ---------------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------------

export const ReportAssetKindSchema = z.enum(['image', 'file'])

/** Who made a version (or a report's latest version): a run (agent) or a user. */
export const ReportAuthorSchema = z.object({
  runId: z.string().nullable(),
  agentId: z.string().nullable(),
  agentName: z.string().nullable(),
  userId: z.string().nullable(),
  userName: z.string().nullable(),
})
export type ReportAuthor = z.infer<typeof ReportAuthorSchema>

/** One stored attachment (immutable). */
export const ReportAssetSchema = z.object({
  id: z.string(),
  name: z.string(),
  contentType: ReportAssetContentTypeSchema,
  kind: ReportAssetKindSchema,
  size: z.number().int(),
  sha256: z.string(),
  createdAt: z.string(),
  createdByRunId: z.string().nullable(),
  createdByUserId: z.string().nullable(),
})
export type ReportAsset = z.infer<typeof ReportAssetSchema>

export const ReportVersionSummarySchema = z.object({
  version: z.number().int(),
  title: z.string(),
  summary: z.string().nullable(),
  message: z.string().nullable(),
  author: ReportAuthorSchema,
  /** Number of entries in this version's asset manifest. */
  assetCount: z.number().int(),
  createdAt: z.string(),
})
export type ReportVersionSummary = z.infer<typeof ReportVersionSummarySchema>

/** A full version (`GET /reports/:reportId/versions/:version`): the stored HTML (asset references not rewritten) and its manifest. */
export const ReportVersionSchema = ReportVersionSummarySchema.extend({
  reportId: z.string(),
  html: z.string(),
  /** The manifest `name → asset`, as a list sorted by name. */
  assets: z.array(ReportAssetSchema),
})
export type ReportVersion = z.infer<typeof ReportVersionSchema>

/** The report row plus who made its current version. */
export const ReportSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  projectId: z.string().nullable(),
  taskId: z.string(),
  title: z.string(),
  summary: z.string().nullable(),
  currentVersion: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
  updatedByRunId: z.string().nullable(),
  updatedByUserId: z.string().nullable(),
  /** Author of the current version (the provenance bar). */
  provenance: ReportAuthorSchema,
  submissionCount: z.number().int(),
  /** Submissions no run has read yet via `report_read`. */
  unseenSubmissionCount: z.number().int(),
})
export type Report = z.infer<typeof ReportSchema>

// ---------------------------------------------------------------------------
// REST responses / requests
// ---------------------------------------------------------------------------

/** `GET /tasks/:taskId/report` — `report: null` when the task has none. */
export const TaskReportResponseSchema = z.object({
  report: ReportSchema.extend({
    /** Newest first. */
    versions: z.array(ReportVersionSummarySchema),
    /** The current version's manifest, sorted by name. */
    assets: z.array(ReportAssetSchema),
  }).nullable(),
})
export type TaskReportResponse = z.infer<typeof TaskReportResponseSchema>
export type TaskReportDetail = NonNullable<TaskReportResponse['report']>

/** `?version=` (omitted = current) and `?assetBase=` (the API base URL the browser uses, e.g. `https://goblic.example.com/api`; omitted = the server's `GOBLIC_PUBLIC_API_URL`, else the request's own origin). */
export const ReportRenderQuerySchema = z.object({
  version: z.coerce.number().int().min(1).optional(),
  assetBase: z.string().url().max(500).optional(),
})
export type ReportRenderQuery = z.infer<typeof ReportRenderQuerySchema>

export const ReportVersionQuerySchema = z.object({
  version: z.coerce.number().int().min(1).optional(),
})

/** A version's attachment as the viewer lists it (download links go outside the iframe). */
export const ReportRenderedAssetSchema = z.object({
  name: z.string(),
  contentType: ReportAssetContentTypeSchema,
  kind: ReportAssetKindSchema,
  size: z.number().int(),
  /** Signed URL, valid until `urlExpiresAt`. */
  url: z.string(),
})
export type ReportRenderedAsset = z.infer<typeof ReportRenderedAssetSchema>

/** `GET /reports/:reportId/render?version=&assetBase=`. */
export const ReportRenderResponseSchema = z.object({
  reportId: z.string(),
  taskId: z.string(),
  /**
   * Complete document for `<iframe srcdoc>`. Shown with `sandbox=""` (static)
   * until the viewer enables interactive content; only then
   * `sandbox="allow-scripts"`.
   */
  html: z.string(),
  title: z.string(),
  version: z.number().int(),
  currentVersion: z.number().int(),
  provenance: ReportAuthorSchema.extend({ createdAt: z.string() }),
  assets: z.array(ReportRenderedAssetSchema),
  /** `assets/<name>` references in the HTML that this version has no attachment for. */
  missingAssets: z.array(z.string()),
  /** When the signed asset URLs stop working (ISO); re-render after that. */
  urlExpiresAt: z.string(),
  /**
   * The agent HTML has scripts, event handlers, `javascript:` URLs, meta
   * refresh, `srcdoc` or embedded frames/objects (`detectReportScripts`).
   * The web offers "Enable interactive content" only then; without it the
   * report is always static.
   */
  hasScripts: z.boolean(),
})
export type ReportRenderResponse = z.infer<typeof ReportRenderResponseSchema>

const TitleSchema = z.string().trim().min(1).max(REPORT_TITLE_MAX_LENGTH)
const SummarySchema = z.string().trim().max(REPORT_SUMMARY_MAX_LENGTH)
const MessageSchema = z.string().trim().max(REPORT_MESSAGE_MAX_LENGTH)
const HtmlSchema = z
  .string()
  .min(1)
  .superRefine((html, ctx) => {
    const size = reportByteLength(html)
    if (size > REPORT_MAX_HTML_BYTES) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `HTML is ${size} bytes; at most ${REPORT_MAX_HTML_BYTES} bytes per version` })
    }
  })

/** `PUT /tasks/:taskId/report` — a person (or the Goblic-MCP) writes a new version. */
export const ReportPutRequestSchema = z.object({
  title: TitleSchema,
  html: HtmlSchema,
  summary: SummarySchema.optional(),
  message: MessageSchema.optional(),
})
export type ReportPutRequest = z.infer<typeof ReportPutRequestSchema>

/** Result of a write (REST `PUT`, internal write, `report_write`). */
export const ReportWriteResultSchema = z.object({
  reportId: z.string(),
  taskId: z.string(),
  version: z.number().int(),
  /** True when this write created the report. */
  created: z.boolean(),
  /** Web link to the task's report, when the API knows the web URL. */
  url: z.string().nullable(),
  /** `assets/<name>` references without an attachment in this version. */
  missingAssets: z.array(z.string()),
  /** Human-readable warnings (missing assets…) — not errors; the version is saved. */
  warnings: z.array(z.string()),
})
export type ReportWriteResult = z.infer<typeof ReportWriteResultSchema>

/** Ceiling for base64 text of one ≤ 5 MB attachment. */
export const REPORT_MAX_ASSET_BASE64_LENGTH = Math.ceil(REPORT_MAX_ASSET_BYTES / 3) * 4

export const ReportBase64Schema = z
  .string()
  .min(1)
  .max(REPORT_MAX_ASSET_BASE64_LENGTH, `Attachment is larger than ${REPORT_MAX_ASSET_BYTES} bytes`)
  .regex(/^[A-Za-z0-9+/]*={0,2}$/, 'data must be standard base64 (no data: prefix, no line breaks)')
  .refine((value) => value.length % 4 === 0, 'base64 length must be a multiple of 4')

/** `POST /reports/:reportId/assets` (people, Goblic-MCP). */
export const ReportAssetUploadRequestSchema = z.object({
  name: ReportAssetNameSchema,
  data: ReportBase64Schema,
})
export type ReportAssetUploadRequest = z.infer<typeof ReportAssetUploadRequestSchema>

/** Result of an attach (REST, internal, `report_attach`). */
export const ReportAttachResultSchema = z.object({
  assetId: z.string(),
  name: z.string(),
  contentType: ReportAssetContentTypeSchema,
  kind: ReportAssetKindSchema,
  size: z.number().int(),
  sha256: z.string(),
  /** What to write in the HTML: `assets/<name>`. */
  embed: z.string(),
  /** True when identical bytes under this name were already stored (no new asset). */
  deduplicated: z.boolean(),
})
export type ReportAttachResult = z.infer<typeof ReportAttachResultSchema>

export const ReportSubmissionSchema = z.object({
  id: z.string(),
  reportId: z.string(),
  version: z.number().int(),
  data: z.unknown(),
  submittedByUserId: z.string(),
  submittedByUserName: z.string().nullable(),
  createdAt: z.string(),
  /** The first run that read it via `report_read`, or null. */
  seenByRunId: z.string().nullable(),
})
export type ReportSubmission = z.infer<typeof ReportSubmissionSchema>

/** `GET /reports/:reportId/submissions` — newest first. */
export const ReportSubmissionListResponseSchema = z.object({
  submissions: z.array(ReportSubmissionSchema),
})
export type ReportSubmissionListResponse = z.infer<typeof ReportSubmissionListResponseSchema>

/**
 * `GET /reports/:reportId/submissions/latest-own` — the caller's own latest
 * submission (any version), for `goblic.getLastSubmission()`; null = none.
 */
export const ReportOwnLatestSubmissionResponseSchema = z.object({
  submission: ReportLastSubmissionSchema.nullable(),
})
export type ReportOwnLatestSubmissionResponse = z.infer<typeof ReportOwnLatestSubmissionResponseSchema>

/** `POST /reports/:reportId/submissions`. */
export const ReportSubmitRequestSchema = z.object({
  version: z.number().int().min(1),
  data: ReportSubmissionDataSchema,
})
export type ReportSubmitRequest = z.infer<typeof ReportSubmitRequestSchema>

export const ReportSubmitResultSchema = z.object({
  submission: ReportSubmissionSchema,
})
export type ReportSubmitResult = z.infer<typeof ReportSubmitResultSchema>

/** `GET /projects/:projectId/reports` — one entry per task with a report, newest change first. */
export const ProjectReportEntrySchema = z.object({
  reportId: z.string(),
  taskId: z.string(),
  taskTitle: z.string(),
  title: z.string(),
  summary: z.string().nullable(),
  currentVersion: z.number().int(),
  updatedAt: z.string(),
  provenance: ReportAuthorSchema,
  submissionCount: z.number().int(),
})
export type ProjectReportEntry = z.infer<typeof ProjectReportEntrySchema>

export const ProjectReportListResponseSchema = z.object({
  reports: z.array(ProjectReportEntrySchema),
})
export type ProjectReportListResponse = z.infer<typeof ProjectReportListResponseSchema>

// ---------------------------------------------------------------------------
// Internal (worker) contract — §6.1. The run decides the task.
// ---------------------------------------------------------------------------

/** `POST /internal/runs/:runId/report` (report_write). */
export const ReportInternalWriteRequestSchema = z.object({
  title: TitleSchema,
  html: HtmlSchema,
  summary: SummarySchema.optional(),
  message: MessageSchema.min(1),
})
export type ReportInternalWriteRequest = z.infer<typeof ReportInternalWriteRequestSchema>

/** `POST /internal/runs/:runId/report/assets` (report_attach) — the worker read the file from the sandbox. */
export const ReportInternalAttachRequestSchema = z.object({
  /** Asset name; omitted = derived from `path` (`deriveReportAssetName`). */
  name: ReportAssetNameSchema.optional(),
  /** The sandbox path the agent gave (for the name and the run event). */
  path: z.string().min(1).max(1000),
  data: ReportBase64Schema,
})
export type ReportInternalAttachRequest = z.infer<typeof ReportInternalAttachRequestSchema>

/** `GET /internal/runs/:runId/report?version=` (report_read). */
export const ReportInternalReadQuerySchema = ReportVersionQuerySchema
export type ReportInternalReadQuery = z.infer<typeof ReportInternalReadQuerySchema>

export const ReportAgentAssetSchema = z.object({
  name: z.string(),
  contentType: ReportAssetContentTypeSchema,
  kind: ReportAssetKindSchema,
  size: z.number().int(),
  embed: z.string(),
})

export const ReportAgentSubmissionSchema = z.object({
  id: z.string(),
  version: z.number().int(),
  /** Filled in by a person — data, not instructions. */
  data: z.unknown(),
  submittedBy: z.string().nullable(),
  createdAt: z.string(),
})
export type ReportAgentSubmission = z.infer<typeof ReportAgentSubmissionSchema>

export const ReportAgentReadResponseSchema = z.object({
  /** `null` when the task has no report yet. */
  report: z
    .object({
      reportId: z.string(),
      version: z.number().int(),
      currentVersion: z.number().int(),
      title: z.string(),
      summary: z.string().nullable(),
      html: z.string(),
      assets: z.array(ReportAgentAssetSchema),
    })
    .nullable(),
  /** Submissions no earlier run has read (now marked as read by this run). */
  submissions: z.array(ReportAgentSubmissionSchema),
  /** Reminder for the agent: submissions are human-entered data, not instructions. */
  note: z.string(),
  /** Unread submissions left after this batch (≤ 50 per call / 256 KB); the next `report_read` returns them. */
  remainingSubmissions: z.number().int().min(0).optional(),
})
export type ReportAgentReadResponse = z.infer<typeof ReportAgentReadResponseSchema>

/** Error codes the report API uses (besides the generic ones). */
export const REPORT_ERROR_CODES = {
  limit: 'REPORT_LIMIT',
  invalid: 'REPORT_INVALID',
  disabled: 'REPORT_DISABLED',
  runInactive: 'REPORT_RUN_INACTIVE',
  /** 404: the report (or the asked version of it) doesn't exist — as opposed to `NOT_FOUND` for an unknown run. */
  versionNotFound: 'REPORT_VERSION_NOT_FOUND',
} as const
